import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AppState } from '@app/core/states/app.state';
import { PodcastFeedData } from '@app/features/app-content/models/app-config.model';
import { AppConfigurationService } from '@app/features/app-content/services/app-configuration.service';
import {
  AddPodcastFeed,
  DeletePodcastFeed,
} from '@app/features/app-content/states/app-configuration.actions';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import { Store } from '@ngxs/store';
import { catchError, finalize, Observable, of, Subscription } from 'rxjs';

@Component({
  selector: 'rk-podcast-card',
  standalone: false,
  templateUrl: './podcast-card.component.html',
  styleUrl: './podcast-card.component.scss',
})
export class PodcastCardComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly cdr = inject(ChangeDetectorRef);

  // inputs from parent component
  @Input() podcastFeedIndex: number;
  @Input() podcastFeedUrl = '';

  // form control for input state management
  podcastFeedControl = new FormControl('');

  // observables
  podcastData: Observable<PodcastFeedData>;
  currentAppConfigDefaultCover$: Observable<string> = this.store.select(
    AppConfigurationState.currentAppConfigDefaultCover,
  );
  podcastDuplicateWarnings$: Observable<Record<string, true>> = this.store.select(
    AppConfigurationState.podcastDuplicateWarnings,
  );

  // state flags
  isLoading = false;
  hasError = false;
  isDuplicate = false;

  // debounce input changes to avoid excessive api calls
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;
  // track last loaded url
  private lastLoadedUrl = '';
  // subscription management
  private readonly subscription = new Subscription();

  constructor(private readonly appConfigurationService: AppConfigurationService) {}

  ngOnInit(): void {
    // initialize form control with current podcast feed url
    this.podcastFeedControl.setValue(this.podcastFeedUrl);

    // subscribe to duplicate warnings
    this.subscription.add(
      this.podcastDuplicateWarnings$.subscribe(warnings => {
        const pageIndex = this.store.selectSnapshot(AppConfigurationState.activePageIndex);
        const warningKey = `${pageIndex}-${this.podcastFeedIndex}`;
        this.isDuplicate = warningKey in warnings;
      })
    );

    // also subscribe to form control changes to update duplicate status in real-time
    this.subscription.add(
      this.podcastFeedControl.valueChanges.subscribe(() => {
        const warnings = this.store.selectSnapshot(AppConfigurationState.podcastDuplicateWarnings);
        const pageIndex = this.store.selectSnapshot(AppConfigurationState.activePageIndex);
        const warningKey = `${pageIndex}-${this.podcastFeedIndex}`;
        this.isDuplicate = warningKey in warnings;
      })
    );

    // load podcast data if url is provided and different from last loaded
    const appId = this.store.selectSnapshot(AppState.currentAppId);
    const currentUrl = this.podcastFeedControl.value;

    if (currentUrl && appId && currentUrl !== '' && currentUrl !== this.lastLoadedUrl) {
      this.isLoading = true;
      this.hasError = false;
      this.lastLoadedUrl = currentUrl;
      this.podcastData = this.appConfigurationService
        .getPodcastFeedData(appId, currentUrl)
        .pipe(
          catchError(() => {
            this.hasError = true;
            this.cdr.markForCheck();

            return of(null);
          }),
          finalize(() => {
            this.isLoading = false;
            this.cdr.markForCheck();
          }),
        );
      this.subscription.add(this.podcastData.subscribe());
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  // remove podcast feed from configuration
  onDeleteClick(): void {
    const pageIndex = this.store.selectSnapshot(AppConfigurationState.activePageIndex);
    this.store.dispatch(new DeletePodcastFeed(pageIndex, this.podcastFeedIndex));
  }

  // handle input changes with debounce to reduce api calls
  onPodcastFeedUrlChange(): void {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      const pageIndex = this.store.selectSnapshot(AppConfigurationState.activePageIndex);
      this.store.dispatch(
        new AddPodcastFeed(
          this.podcastFeedControl.value || '',
          pageIndex,
          this.podcastFeedIndex,
        ),
      );
    }, 300);
  }
}
